import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FetchBaseQueryError, skipToken } from '@reduxjs/toolkit/dist/query';
import Container from '@mui/material/Container';
import { useAppSelector } from '../../app/hooks';
import { useGetClinicAccountsQuery } from '../../app/services/account';
import { useGetBillingSettingsQuery } from '../../app/services/billing';
import {
  useLazyRequestCoCMAssessmentTypesQuery,
  useProcessSiteOptInCoCMMutation,
} from '../../app/services/interview';
import {
  useAddSiteMutation,
  useEditSiteMutation,
  useGetSiteInfoQuery,
} from '../../app/services/site';
import { useToast } from '../../app/toast';
import Loading from '../../common/components/Loading';
import PageHeader from '../../common/components/PageHeader';
import RoundedContainer from '../../common/components/RoundedContainer';
import SiteInfoCard from '../../common/components/SiteInfoCard';
import DashboardLayout from '../../common/layouts/Dashboard';
import { ReportType } from '../../common/utils/assessmentType';
import { addBreadcrumb, BreadcrumbType } from '../../common/utils/editBreadcrums';
import { HttpStatusCode } from '../../common/utils/httpStatusCode';
import { Role } from '../../common/utils/loginRole';
import { getPageTitle } from '../../common/utils/pageUtils';
import { canAddSite, canEditSite, isClinicAdmin } from '../../common/utils/permissionUtils';
import { CoCMAssessmentTypeItem } from '../../types/AccountType';
import { SiteInfo } from '../../types/ClinicSiteType';
import { PageProps } from '../../types/PageProps';
import { selectUser } from '../auth/authSlice';
import ErrorPage from '../error/ErrorPage';
import SiteForm from './SiteForm';

export default function AddOrEditSitePage({ breadcrumbs }: PageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const params = useParams();
  const siteId: string | undefined = params.siteId;

  const currentUser = useAppSelector(selectUser);
  const canEdit = canEditSite(currentUser.roleId);
  const canAdd = canAddSite(currentUser.roleId);
  const [addSite] = useAddSiteMutation();
  const [editSite] = useEditSiteMutation();
  const [processSiteOptInCoCM] = useProcessSiteOptInCoCMMutation();

  const { data: clinicAccountData, isLoading: isAccountLoading } = useGetClinicAccountsQuery(
    undefined,
    {
      skip: (siteId && !canEdit) || (!siteId && !canAdd),
    }
  );
  const { data: billingSettingsData } = useGetBillingSettingsQuery(
    isClinicAdmin(currentUser.roleId) || currentUser.roleId === Role.SuperAdmin
      ? {
          siteId: siteId ? parseInt(siteId) : undefined,
        }
      : skipToken
  );
  const { data: siteInfo, error } = useGetSiteInfoQuery(
    siteId && canEdit ? { siteId: parseInt(siteId) } : skipToken
  );

  const [
    triggerRequestCoCMAssessmentTypes,
    { data: coCMAssessmentTypes, isLoading: isLoadingCoCMAssessmentType },
  ] = useLazyRequestCoCMAssessmentTypesQuery();

  const { state } = useLocation();
  const { navBackUrl } = !!state && state;
  const newBreadcrumb: BreadcrumbType = {
    text: t('clinicSite.manageSites.title'),
    link: navBackUrl || `/dashboard/sites`,
  };

  const onFormSubmit = async (data: SiteInfo, siteId?: number, successCallback?: () => void) => {
    if (data && !data.intakePacketInterviews) {
      data.intakePacketInterviews = undefined;
    }
    if (data && !data.intakePacketDefaultProvider) {
      data.intakePacketDefaultProvider = undefined;
    }
    if (data && !data.locationId) {
      data.locationId = undefined;
    }
    if (siteId) {
      if (!data.optInCoCM) {
        data.screeningAssessments = undefined;
        data.diagnosticAssessments = undefined;
        data.defaultCareManagerId = undefined;
      }
      if (!data.defaultCareManagerId) {
        data.defaultCareManagerId = undefined;
      }
      await editSite({ ...data, id: siteId }).unwrap();

      if (data.optInCoCM) {
        processSiteOptInCoCM({ siteId }).unwrap();
      }
    } else {
      await addSite(data).unwrap();
      navigate(`/dashboard/sites`);
    }

    if (successCallback) {
      successCallback();
    }

    toast.publish(
      (siteId && t('clinicSite.manageSites.updateSuccess')) ||
        t('clinicSite.manageSites.addSuccess'),
      'success'
    );
  };

  if (siteId && !canEdit) {
    return <ErrorPage statusCode={HttpStatusCode.Forbidden} />;
  }
  if (error) {
    return <ErrorPage statusCode={(error as FetchBaseQueryError).status} />;
  }

  return (
    <DashboardLayout breadcrumbs={addBreadcrumb(breadcrumbs!, newBreadcrumb)}>
      <Helmet>
        <title>
          {siteId
            ? getPageTitle(t('clinicSite.manageSites.editSite'))
            : getPageTitle(t('clinicSite.manageSites.addSite'))}
        </title>
      </Helmet>
      <Container maxWidth="xl">
        <PageHeader
          sx={{ mt: 1, mb: 2 }}
          headerText={
            siteId ? t('clinicSite.manageSites.editSite') : t('clinicSite.manageSites.addSite')
          }
          backUrl={navBackUrl || `/dashboard/sites`}
        />
      </Container>

      {siteId && (
        <RoundedContainer sx={{ py: 2, mb: 2 }}>
          <SiteInfoCard data={siteInfo!} />
        </RoundedContainer>
      )}

      {isAccountLoading ? (
        <Loading />
      ) : (
        <SiteForm
          siteId={siteId ? parseInt(siteId) : 0}
          data={siteInfo}
          clinicAccounts={clinicAccountData?.clinicAccounts || []}
          billingSettings={billingSettingsData}
          onFormSubmit={onFormSubmit}
          triggerRequestCoCMAssessmentTypes={triggerRequestCoCMAssessmentTypes}
          coCMAssessmentTypes={coCMAssessmentTypes?.assessmentTypes?.reduce(
            (
              acc: { [x: string]: CoCMAssessmentTypeItem },
              assessmentType: CoCMAssessmentTypeItem
            ) => {
              acc[
                `${assessmentType.id}-${
                  assessmentType.type === ReportType.Interview
                    ? ReportType.Interview
                    : ReportType.Monitor
                }`
              ] = assessmentType;
              return acc;
            },
            {} as Record<string, typeof coCMAssessmentTypes.assessmentTypes[0]>
          )}
          isLoadingCoCMAssessmentType={isLoadingCoCMAssessmentType}
        />
      )}
    </DashboardLayout>
  );
}
